import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from 'react-redux';
import useConfirm from 'hooks/useConfirm';
import { RootState } from 'redux/reducers';
import { useSnackbar } from 'notistack';
import Grid from '@mui/material/Grid2';
import MaterialTable, { MTableToolbar, Column } from 'material-table';
import { collection } from 'firebase/firestore';
import Filter from 'views/triage/components/Filter';
import DateRangeSelector from 'views/triage/components/DateRangeSelector';
import { Box, Checkbox, Container, Typography } from '@mui/material';
import { firestoreInstance } from 'config/ApiService';
import AuthActionCreators from 'redux/creators/AuthActionCreators';
import { LOG_TYPES } from 'utils/Logger';
import CursorQuery, { QueryFilter } from 'core/utils/CursorQuery';
import TruncatedText from './components/TruncatedText';
import MultipleFilter from './components/MultipleFilter';
import CaseHandler from 'pages/Engage/backend/CaseHandler';
import { downloadPdfReport, PdfResponse } from 'services/pdfService';

const dischargeCodes = [
    'failed_to_onboard',
    'episode_closed',
    'episode_complete',
    'onward_referral',
    'other'
];

const DischargeFromEngageV2 = () => {
    const [startDate, setStartDate] = useState(dayjs(new Date()).startOf('day'));
    const [profiles, setProfiles] = useState<any[]>([]);
    const [endDate, setEndDate] = useState(dayjs(new Date()).endOf('day'));
    const [actionedFilter, setActionedFilter] = useState('notActioned');
    const [dischargeCodeFilter, setDischargeCodeFilter] = useState<string[]>(['All']);
    const [loading, setLoading] = useState(false);

    const client = useSelector((state: RootState) => state.AuthReducer.organisation);

    const { isConfirmed } = useConfirm();

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const fetchTreatmentPlan = useCallback(async () => {
        try {
            setLoading(true);
            const cursorQuery = new CursorQuery(
                collection(firestoreInstance(), 'phioengage_user_profiles')
            );
            const filtersBase = [
                { field: 'case_status', operator: '==', value: 'discharged' },
                { field: 'customer_id', operator: '==', value: client.id },
                {
                    field: 'discharge_date',
                    operator: '>=',
                    value: startDate.toDate()
                },
                {
                    field: 'discharge_date',
                    operator: '<=',
                    value: endDate.toDate()
                }
            ] as QueryFilter[];

            const actionedFilterQuery =
                actionedFilter !== 'all'
                    ? [
                          {
                              field: 'actioned_discharge',
                              operator: '==',
                              value: actionedFilter === 'actioned' ? true : false
                          }
                      ]
                    : [];

            // 'in' operator is limited to 10 queries
            const dischargeCodeFilterQuery =
                !dischargeCodeFilter.includes('All') &&
                dischargeCodeFilter?.length !== 0 &&
                dischargeCodeFilter?.length <= 10 &&
                dischargeCodeFilter?.length !== dischargeCodes?.length
                    ? [
                          {
                              field: 'discharge_reason',
                              operator: 'in',
                              value: dischargeCodeFilter
                          }
                      ]
                    : [];
            const filters = [
                ...filtersBase,
                ...actionedFilterQuery,
                ...dischargeCodeFilterQuery
            ] as QueryFilter[];
            const data = await cursorQuery.getAllDocuments({
                filters,
                queryOrderBy: { field: 'discharge_date', direction: 'desc' },
                queryLimit: 100
            });
            const dataMap = data?.map((doc) => {
                const docData = doc.data();
                return {
                    ...docData,
                    id: doc.id,
                    name: `${docData.first_name || ''} ${docData.last_name || ''}`,
                    start_treatment_date:
                        docData?.start_date && typeof docData?.start_date !== 'string'
                            ? dayjs(docData.start_date.toDate()).format('DD/MM/YYYY HH:mm')
                            : '',
                    discharged_date: docData?.case_closed_at
                        ? dayjs(docData.case_closed_at.toDate()).format('DD/MM/YYYY HH:mm')
                        : ''
                };
            }) as any[];
            setProfiles(dataMap);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [actionedFilter, client.id, dischargeCodeFilter, endDate, startDate]);

    const handleActionCheckboxChange = async (data: any, checkedState: boolean) => {
        try {
            if (data.actioned_discharged && !checkedState) {
                const msg = (
                    <Box sx={{ textAlign: 'center' }}>
                        <p>
                            <b>{`UID: ${data.id}`}</b> <br />
                            <b>Full Name:</b> {`${data.name || ''}`}
                            <br />
                            <br />
                            <span>
                                This patient has been marked as actioned. Are you sure you want to
                                change the status for this patient to “Not Actioned”?
                            </span>
                        </p>
                    </Box>
                );
                // We should ask for confirmation regardless of the action
                const confirm = await isConfirmed(msg);
                if (!confirm) {
                    fetchTreatmentPlan();
                    return;
                }
            }
            // Use US service to update the actioned status
            const handler = new CaseHandler(data.region);
            await handler.reviewDischarge(data.id, { actioned_discharged: checkedState });
            // Audit log
            dispatch(
                AuthActionCreators.logForAudit({
                    target: {
                        id: data.id,
                        description: checkedState
                            ? 'Discharge actioned'
                            : 'Discharge action removed',
                        affectedUser: data.id
                    },
                    type: LOG_TYPES.WORKFLOW
                })
            );
            fetchTreatmentPlan();
        } catch (e) {
            console.error(e);
        }
    };

    const columns: Column<any>[] = [
        {
            field: 'actioned_discharge',
            title: 'Reviewed?',
            type: 'boolean',
            align: 'center',
            render: (rowData: any) => {
                return (
                    <Box sx={{ width: '100px', display: 'flex', justifyContent: 'center' }}>
                        <Checkbox
                            checked={rowData.actioned_discharge}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                handleActionCheckboxChange(rowData, e.target.checked);
                            }}
                            color="primary"
                            inputProps={{ 'aria-label': 'actioned checkbox' }}
                        />
                    </Box>
                );
            },
            searchable: false,
            sorting: false
        },
        {
            field: 'id',
            title: 'Engage UID',
            searchable: true,
            editable: 'never',
            sorting: false
        },
        {
            field: 'discharge_reason',
            title: 'Discharge Code',
            searchable: false,
            sorting: false,
            cellStyle: { minWidth: '200px' },
            render(rowData: any) {
                const code = rowData?.discharge_reason;
                const formattedCode = code
                    ? code
                          .split('_')
                          .map(
                              (word: string) =>
                                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                          )
                          .join(' ')
                    : 'N/A';
                return <span>{formattedCode}</span>;
            }
        },
        {
            field: 'name',
            title: 'Name',
            searchable: true,
            cellStyle: { minWidth: '200px' }
        },
        {
            field: 'date_of_birth',
            title: 'DoB',
            searchable: true,
            cellStyle: { minWidth: '150px' },
            emptyValue: 'missing'
        },
        {
            field: 'start_date',
            title: 'Start Date',
            cellStyle: { minWidth: '180px' },
            searchable: false,
            sorting: false,
            render: (rowData: any) => {
                if (rowData?.start_date) {
                    // convert to date
                    const startDate =
                        typeof rowData.start_date === 'string'
                            ? dayjs(rowData.start_date)
                            : dayjs(rowData.start_date.toDate());
                    return startDate.format('DD/MM/YYYY HH:mm');
                }
            }
        },
        {
            field: 'discharged_date',
            title: 'Discharge Date',
            searchable: false,
            sorting: false,
            cellStyle: { minWidth: '180px' },
            render: (rowData: any) => {
                if (rowData?.discharge_date) {
                    // convert to date
                    const dischargedDate =
                        typeof rowData.discharge_date === 'string'
                            ? dayjs(rowData.discharge_date)
                            : dayjs(rowData.discharge_date.toDate());
                    return dischargedDate.format('DD/MM/YYYY HH:mm');
                }
            }
        },
        {
            field: 'customer_note',
            title: 'Customer Notes',
            emptyValue: 'no notes added',
            render: (rowData: any) => {
                if (rowData?.customer_note) {
                    return <TruncatedText text={rowData?.customer_note} />;
                }
            },
            searchable: false,
            sorting: false,
            cellStyle: { minWidth: '250px' }
        }
    ];

    const handleDownloadPDF = async (rowData: any) => {
        setLoading(true);
        if (!rowData?.id) {
            return;
        }
        const result = await new CaseHandler(rowData.region).getdischargeReport(rowData.id);
        const { pdfAsBase64String, status } = result.data as PdfResponse;
        if (status === 200) {
            await downloadPdfReport(
                pdfAsBase64String,
                `Patient Report - ${rowData.first_name} ${rowData.last_name}.pdf`
            );
            enqueueSnackbar('PDF Downloaded', { variant: 'success' });
            setLoading(false);
        } else {
            enqueueSnackbar('There was an issue downloading this PDF, please try again.', {
                variant: 'error'
            });
            setLoading(false);
        }
    };

    const ToolbarFilters = () => (
        <Container maxWidth="xl">
            <Grid container spacing={2} mt={2}>
                <Grid size={{ xs: 6, md: 5, lg: 4 }}>
                    <DateRangeSelector
                        label="Discharge Date (Start)"
                        defaultDate={startDate}
                        onDateChange={(val) => {
                            setStartDate(dayjs(val).startOf('day'));
                        }}
                        fullWidth
                    />
                </Grid>
                <Grid size={{ xs: 6, md: 5, lg: 4 }}>
                    <DateRangeSelector
                        label="Discharge Date (End)"
                        defaultDate={endDate}
                        onDateChange={(val) => {
                            setEndDate(dayjs(val).endOf('day'));
                        }}
                        fullWidth
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2} mt={2} mb={3}>
                <Grid size={{ xs: 12, md: 5, lg: 4 }}>
                    <Filter
                        label="Reviewed Status"
                        options={[
                            { id: 'all', name: 'All' },
                            { id: 'actioned', name: 'Actioned' },
                            { id: 'notActioned', name: 'Not Actioned' }
                        ]}
                        onChange={setActionedFilter}
                        fullWidth
                        value={actionedFilter}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 7, lg: 8 }}>
                    <MultipleFilter
                        label="Discharge code"
                        options={dischargeCodes}
                        fullWidth
                        onSelected={(selected: string[]) => setDischargeCodeFilter(selected)}
                        selectedValue={dischargeCodeFilter}
                        showHelperOnlyOnError
                    />
                </Grid>
            </Grid>
        </Container>
    );

    useEffect(() => {
        (async () => {
            await fetchTreatmentPlan();
        })();
    }, [fetchTreatmentPlan]);

    return (
        <MaterialTable
            title="Discharged from Engage"
            data={profiles}
            isLoading={loading}
            actions={[
                {
                    icon: 'save_alt',
                    tooltip: 'Download pdf',
                    onClick: (evt, rowData: any) => {
                        handleDownloadPDF(rowData);
                    }
                }
            ]}
            components={{
                Toolbar: (props) => (
                    <>
                        <MTableToolbar {...props} />
                        <ToolbarFilters />
                    </>
                )
            }}
            columns={columns}
            options={{
                pageSize: 10,
                pageSizeOptions: [10, 20, 30],
                searchFieldVariant: 'outlined',
                searchFieldStyle: { width: '350px' },
                exportButton: true,
                exportAllData: true,
                headerStyle: {
                    verticalAlign: 'bottom',
                    paddingBottom: '16px',
                    paddingRight: '16px'
                },
                exportFileName: `Discharged from Engage ${dayjs(startDate).format(
                    'DD/MM/YYYY'
                )} - ${dayjs(endDate).format('DD/MM/YYYY')}`
            }}
            localization={{
                toolbar: {
                    searchPlaceholder: 'Search by UID, Name or DOB'
                }
            }}
            detailPanel={(rowData) => {
                return (
                    <Container maxWidth="xl">
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 8, md: 5 }} offset={{ xs: 1, md: 2 }}>
                                <Box my={3}>
                                    <Typography
                                        variant="h6"
                                        component="p"
                                        color="secondary"
                                        gutterBottom
                                    >
                                        Customer Notes
                                    </Typography>
                                    <Typography variant="body1" component="p">
                                        {rowData?.customer_note || 'no notes'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                );
            }}
        />
    );
};

export default DischargeFromEngageV2;
